import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface StatusPageFormProps {
  serverErrors?: ServerErrors;
}

export const StatusPageForm = ({serverErrors}: StatusPageFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.api_key"
            label="API Key"
            required={true}
            tooltip="Listed on the Manage Account / API page."
            maxLength={256}
            autoFocus={true}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.page"
            label="Page ID"
            required={true}
            tooltip="Listed on the Manage Account / API page."
            maxLength={12}
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.component"
            label="Component ID"
            required={false}
            tooltip="Component to update with availability status, see instructions below."
            maxLength={12}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.metric"
            label="Metric ID"
            required={false}
            tooltip="Metric to update with response time data. Listed in Public Metrics / Edit Metric / Advanced Options."
            maxLength={12}
          />
        </div>
      </div>
      <SetupInfo metricsWarning={true}>
        <div className="p-2">
          In StatusPage, click the bottom-left user icon menu and select "API Info". Copy the "API User Key" and "Page
          ID" values into the corresponding fields above.
        </div>
        <div className="p-2">
          To update a component on alerts, click "Components" and find the Component ID by hovering over the "Edit"
          link, or click "Edit" and copy it from the page's URL. (The Component ID is the last part of the URL, eg
          "y0jf83vrf5qb").
        </div>
        <div className="p-2">
          To update a public metric with response times, click on "Your Page", "System Metrics" and "Add a Metric".
          Select "I'll submit my own data for this metric" and provide a metric name and unit. On the following page
          copy the "metric_id" value into the corresponding field above.
        </div>
        <div className="p-2">
          Alternatively, to find the Metric ID of an existing metric, click the "Edit" link beside your metric, go to
          the "Advanced Options" tab and copy the Metric ID value shown there.
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/115002561165-Configuring-StatusPage-io">
            View {IntegrationModuleInfo[IntegrationModules.STATUSPAGE].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
